import React from 'react';
import { BellOutlined, CloseOutlined, ReadOutlined } from '@ant-design/icons';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import { useDebouncedCallback } from 'use-debounce';
import { Avatar, Badge, Button, List, Popover, Typography } from 'antd';
import { useEffectWithPrev } from '@owl-frontend/components';
import type { Notification } from '../../api/notifications/interface';
import { Locale } from '../../context/AppProvider';
import { globalBus } from '../../context/event-bus';
import useNotificationsHook from '../../data/notifications/useNotificationsHook';
import { NOTIFICATION_EMIT_EVENTS } from '../../data/ws/logic';
import useClaimOverviewHook from '../ClaimOverviewContainer/useClaimOverviewHook';
import feedbackStyles from '../FeedbackContainer/FeedbackComponents.module.scss';
import styles from './NotificationsContainer.module.scss';

const SNAKE_CASE = /^[a-z0-9]+(?:_[a-z0-9]+)*(?::[a-z0-9]+)?$/;
const NotificationClaimMetadata: React.FC<{ dossierId: string }> = ({
  dossierId,
}) => {
  const [{ claimMetadata }, handler] = useClaimOverviewHook(dossierId);

  React.useEffect(() => {
    if (claimMetadata) {
      return;
    }

    handler.fetchClaim();
  }, [claimMetadata, handler]);

  if (!claimMetadata) {
    return <>-</>;
  }

  return (
    <>{`${claimMetadata.dossier_name} • ${claimMetadata.client_dossier_id}`}</>
  );
};

// To account for old and new template type path
// Old template path: {template_type: 'CME Discussion Forum'}
// New template path: {template_type: 'forum_discussion'}
export const formatTemplateType = (
  templateType: string | undefined,
  messages: { [key: string]: string }
): string => {
  if (!templateType) {
    return '';
  }
  const template_key = `claim_overview.templates.template_type.${templateType}`;
  return messages[
    SNAKE_CASE.test(templateType) && messages[template_key]
      ? template_key
      : 'claim_overview.templates.template_type.default'
  ];
};

const NotificationsListComponent: React.FC<{
  notifications: Notification[];
  count: number;
  readNotification(notification_id: string): void;
  dismissNotification(notification_id: string): void;
}> = ({ notifications = [], readNotification, dismissNotification }) => {
  const intl = useIntl();
  const { messages } = React.useContext(Locale);
  const navigate = useNavigate();

  return (
    <div id="scrollableDiv" className={styles.listContainer}>
      <Typography.Paragraph strong type="secondary">
        {messages['notifications.title']}
      </Typography.Paragraph>
      {!notifications || notifications.length === 0 ? (
        <Typography.Paragraph type="secondary">
          {messages['notifications.noNewNotifications']}
        </Typography.Paragraph>
      ) : (
        <List
          itemLayout="horizontal"
          dataSource={notifications}
          renderItem={(notification) => (
            <List.Item
              key={notification.notification_id}
              className={styles.listItem}
              onClick={() => {
                readNotification(notification.notification_id);
                navigate(notification.notification_link_to);
              }}
            >
              <List.Item.Meta
                avatar={
                  <Badge dot={!notification.is_read}>
                    <Avatar
                      className={clsx(
                        feedbackStyles.feedbackButton,
                        feedbackStyles.generalFeedbackButton
                      )}
                      icon={<ReadOutlined />}
                    />
                  </Badge>
                }
                title={
                  notification.notification_content_path
                    ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      intl.formatMessage(
                        { id: notification.notification_content_path },
                        {
                          ...notification.notification_content_values,
                          template_type: formatTemplateType(
                            notification.notification_content_values
                              ?.template_type,
                            messages
                          ),
                          b: (val: string[]) => (
                            <Typography.Text
                              className={styles.strongText}
                              strong
                            >
                              {val}
                            </Typography.Text>
                          ),
                        }
                      )
                    : notification.notification_content
                }
                description={
                  !notification.dossier_id ? (
                    '-'
                  ) : (
                    <NotificationClaimMetadata
                      dossierId={notification.dossier_id}
                    />
                  )
                }
              />
              <Button
                icon={<CloseOutlined />}
                size="small"
                type="text"
                shape="circle"
                onClick={(event) => {
                  event.stopPropagation();
                  dismissNotification(notification.notification_id);
                }}
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );
};

const NotificationsContainer = () => {
  const { broadcastMessage } = React.useContext(Locale);
  const [
    {
      notifications = [],
      count,
      actions: { setNotificationStatus },
    },
    notificationHandler,
  ] = useNotificationsHook();

  const invalidateTemplates = useDebouncedCallback(() => {
    broadcastMessage({
      id: 'claim_overview.templates.generate.success',
      type: 'success',
    });
    notificationHandler.fetchNotifications();
  }, 500);

  const invalidateNotifications = useDebouncedCallback(() => {
    broadcastMessage({
      id: 'claim_overview.templates.generate.error',
      type: 'error',
    });
    notificationHandler.fetchNotifications();
  }, 500);

  React.useEffect(() => {
    notificationHandler.fetchNotifications();
  }, [notificationHandler]);

  React.useEffect(() => {
    globalBus.on(
      NOTIFICATION_EMIT_EVENTS.InvalidateTemplates,
      invalidateTemplates
    );

    globalBus.on(
      NOTIFICATION_EMIT_EVENTS.TemplateGenerationError,
      invalidateNotifications
    );

    return () => {
      globalBus.off(
        NOTIFICATION_EMIT_EVENTS.InvalidateTemplates,
        invalidateTemplates
      );

      globalBus.off(
        NOTIFICATION_EMIT_EVENTS.TemplateGenerationError,
        invalidateNotifications
      );
    };
  }, [invalidateNotifications, invalidateTemplates]);

  useEffectWithPrev(
    (prevStatus) => {
      if (prevStatus !== 'pending') {
        return;
      }

      if (setNotificationStatus.status === 'fulfilled') {
        notificationHandler.fetchNotifications();
      }
    },
    [setNotificationStatus.status, notificationHandler]
  );

  return (
    <Badge dot={notifications.some((n) => !n.is_read)}>
      <Popover
        overlayClassName={styles.popover}
        content={
          <NotificationsListComponent
            notifications={notifications}
            count={count}
            readNotification={notificationHandler.readNotification}
            dismissNotification={notificationHandler.dismissNotification}
          />
        }
        trigger="click"
        placement="bottomLeft"
      >
        <Avatar
          className={clsx(
            feedbackStyles.feedbackButton,
            feedbackStyles.generalFeedbackButton
          )}
          shape="circle"
          size={28}
          icon={<BellOutlined style={{ fontSize: '14px' }} />}
        />
      </Popover>
    </Badge>
  );
};

export default NotificationsContainer;
